import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { getErrMsg } from '../../lib/errorMsg';
import { ImagePlus, Trash2, FileText, Download, Loader2, X } from 'lucide-react';

const ACCEPT = 'image/png,image/jpeg,image/jpg,image/webp,image/gif,application/pdf';
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif', 'application/pdf'];
const ALLOWED_EXTS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'pdf'];
const MAX_BYTES = 10 * 1024 * 1024;

const extOf = (name) => (name && name.includes('.') ? name.split('.').pop().toLowerCase() : '');
const isPdfFile = (f) => (f.type || '').toLowerCase() === 'application/pdf' || extOf(f.name) === 'pdf';
const okType = (f) => ALLOWED_TYPES.includes((f.type || '').toLowerCase()) || ALLOWED_EXTS.includes(extOf(f.name));

/**
 * Gallery of images + PDF drawings for a part.
 * - Edit mode (partId set): reads/writes the server (admin can upload/delete).
 * - Pending mode (no partId, on "Add New Part"): buffers files locally and reports
 *   them via onPendingChange so the parent uploads them right after the part is created.
 */
export function PartImagesPanel({ partId, onPendingChange = () => {} }) {
  const { user } = useAuth();
  const canManage = user?.role === 'admin';
  const isPending = !partId;

  // Edit-mode (server) state
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [thumbs, setThumbs] = useState({}); // image_id -> objectURL
  const thumbsRef = useRef({});

  // Pending-mode (local buffer) state — {key, file, url, is_pdf, name}
  const [pending, setPending] = useState([]);
  const pendingRef = useRef([]);

  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);

  const revokeThumbs = useCallback(() => {
    Object.values(thumbsRef.current).forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* already revoked */ } });
    thumbsRef.current = {};
  }, []);
  const revokePending = useCallback(() => {
    pendingRef.current.forEach((p) => { if (p.url) { try { URL.revokeObjectURL(p.url); } catch { /* already revoked */ } } });
  }, []);

  const load = useCallback(async () => {
    if (!partId) return;
    setLoading(true);
    try {
      const res = await api.get(`/parts/${partId}/images`);
      const list = res.data || [];
      setImages(list);
      revokeThumbs();
      const next = {};
      await Promise.all(list.filter((im) => !im.is_pdf).map(async (im) => {
        try {
          const r = await api.get(`/part-images/${im.image_id}/raw`, { responseType: 'blob' });
          next[im.image_id] = URL.createObjectURL(r.data);
        } catch { /* ignore individual thumbnail failures */ }
      }));
      thumbsRef.current = next;
      setThumbs(next);
    } catch (err) {
      toast.error(getErrMsg(err));
    } finally {
      setLoading(false);
    }
  }, [partId, revokeThumbs]);

  useEffect(() => {
    load();
    return () => { revokeThumbs(); revokePending(); };
  }, [load, revokeThumbs, revokePending]);

  const pickFiles = () => fileRef.current?.click();

  const validate = (f) => {
    if (!okType(f)) { toast.error(`${f.name}: only images (PNG/JPG/WEBP/GIF) or PDF allowed`); return false; }
    if (f.size > MAX_BYTES) { toast.error(`${f.name}: too large (max 10 MB)`); return false; }
    return true;
  };

  // ---- Pending mode (create) ----
  const addPending = (files) => {
    const valid = files.filter(validate);
    if (!valid.length) return;
    const additions = valid.map((file) => ({
      key: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      file,
      is_pdf: isPdfFile(file),
      name: file.name,
      url: isPdfFile(file) ? null : URL.createObjectURL(file),
    }));
    const updated = [...pendingRef.current, ...additions];
    pendingRef.current = updated;
    setPending(updated);
    onPendingChange(updated.map((p) => p.file));
  };
  const removePending = (key) => {
    const target = pendingRef.current.find((p) => p.key === key);
    if (target?.url) { try { URL.revokeObjectURL(target.url); } catch { /* already revoked */ } }
    const updated = pendingRef.current.filter((p) => p.key !== key);
    pendingRef.current = updated;
    setPending(updated);
    onPendingChange(updated.map((p) => p.file));
  };

  // ---- Edit mode (server) ----
  const uploadToServer = async (files) => {
    const valid = files.filter(validate);
    if (!valid.length) return;
    setUploading(true);
    let ok = 0;
    try {
      for (const f of valid) {
        const fd = new FormData();
        fd.append('file', f);
        try {
          await api.post(`/parts/${partId}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          ok += 1;
        } catch (err) {
          toast.error(`${f.name}: ${getErrMsg(err)}`);
        }
      }
      if (ok) toast.success(`${ok} file${ok > 1 ? 's' : ''} uploaded`);
      await load();
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      if (isPending) addPending(files);
      else await uploadToServer(files);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDelete = async (im) => {
    if (!window.confirm(`Remove "${im.label || im.filename}"?`)) return;
    try {
      await api.delete(`/part-images/${im.image_id}`);
      toast.success('Removed');
      await load();
    } catch (err) {
      toast.error(getErrMsg(err));
    }
  };

  const handleDownload = async (im) => {
    try {
      const r = await api.get(`/part-images/${im.image_id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = im.filename || 'drawing';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrMsg(err));
    }
  };

  const count = isPending ? pending.length : images.length;

  return (
    <div className="mt-4" data-testid="part-images-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <label className="text-xs font-bold uppercase text-slate-500">Images & Drawings</label>
          <p className="text-[10px] text-slate-400 mt-0.5">
            {isPending
              ? 'Photos and PDF drawings — uploaded when you save the part (max 10 MB each)'
              : 'Photos and PDF drawings for this part (max 10 MB each)'}
          </p>
        </div>
        {canManage && (
          <>
            <input ref={fileRef} type="file" accept={ACCEPT} multiple className="hidden"
              onChange={handleFiles} data-testid="part-image-input" />
            <button type="button" onClick={pickFiles} disabled={uploading}
              data-testid="part-image-upload-btn"
              className="text-xs font-semibold text-orange-500 hover:text-orange-600 flex items-center gap-1 disabled:opacity-50">
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />}
              {uploading ? 'Uploading…' : 'Add Image / PDF'}
            </button>
          </>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-400 py-3"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : count === 0 ? (
        <div className="text-center py-5 border border-dashed border-slate-200 rounded-sm text-xs text-slate-400">
          No images or drawings yet{canManage ? ' — use “Add Image / PDF”.' : '.'}
        </div>
      ) : isPending ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="part-image-grid">
          {pending.map((p) => (
            <div key={p.key} className="relative group border border-slate-200 rounded-sm overflow-hidden bg-slate-50">
              {p.is_pdf ? (
                <div className="w-full h-24 flex flex-col items-center justify-center gap-1 text-slate-500">
                  <FileText size={26} className="text-red-500" />
                  <span className="text-[9px] px-1 text-center truncate w-full">{p.name}</span>
                </div>
              ) : (
                <img src={p.url} alt={p.name} className="w-full h-24 object-cover" />
              )}
              <div className="flex items-center justify-between px-1.5 py-1 bg-white border-t border-slate-100">
                <span className="text-[9px] text-amber-500 font-semibold">PENDING</span>
                {canManage && (
                  <button type="button" onClick={() => removePending(p.key)} title="Remove"
                    className="text-slate-400 hover:text-red-500"><Trash2 size={11} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="part-image-grid">
          {images.map((im) => (
            <div key={im.image_id} className="relative group border border-slate-200 rounded-sm overflow-hidden bg-slate-50">
              {im.is_pdf ? (
                <button type="button" onClick={() => handleDownload(im)} title={im.filename}
                  className="w-full h-24 flex flex-col items-center justify-center gap-1 text-slate-500 hover:bg-slate-100">
                  <FileText size={26} className="text-red-500" />
                  <span className="text-[9px] px-1 text-center truncate w-full">{im.label || im.filename}</span>
                </button>
              ) : thumbs[im.image_id] ? (
                <button type="button" onClick={() => setLightbox(thumbs[im.image_id])} title={im.filename}
                  className="block w-full h-24">
                  <img src={thumbs[im.image_id]} alt={im.label || im.filename} className="w-full h-24 object-cover" />
                </button>
              ) : (
                <div className="w-full h-24 flex items-center justify-center text-slate-300"><Loader2 size={16} className="animate-spin" /></div>
              )}
              <div className="flex items-center justify-between px-1.5 py-1 bg-white border-t border-slate-100">
                <span className="text-[9px] text-slate-400 truncate">{im.is_pdf ? 'PDF' : 'IMG'}</span>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => handleDownload(im)} title="Download"
                    className="text-slate-400 hover:text-slate-700"><Download size={11} /></button>
                  {canManage && (
                    <button type="button" onClick={() => handleDelete(im)} title="Remove"
                      data-testid={`part-image-delete-${im.image_id}`}
                      className="text-slate-400 hover:text-red-500"><Trash2 size={11} /></button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <button type="button" className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)}>
            <X size={22} />
          </button>
          <img src={lightbox} alt="Preview" className="max-w-full max-h-full object-contain rounded shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
